import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "layouts/AppLayout";
import Icon from "components/AppIcon";
import { ROUTES } from "config/navConfig";
import { ROLES } from "config/roleConfig";
import { useRole } from "context/RoleContext";
import Button from "components/ui/Button";
import QuickActionsPanel from "components/ui/QuickActionsPanel";
import OpportunityHeader from "./components/OpportunityHeader";
import TabNavigation from "./components/TabNavigation";
import OverviewTab from "./components/OverviewTab";
import FinancialsTab from "./components/FinancialsTab";
import KeyRisksTab from "./components/KeyRisksTab";
import ESGTab from "./components/ESGTab";
import DocumentsTab from "./components/DocumentsTab";
import DealTermsTab from "./components/DealTermsTab";
import ICMemoView from "./components/ICMemoView";

const OPPORTUNITY = {
  id: "opp-001",
  name: "Northumbria Offshore Wind Phase II",
  sector: "Renewable Energy",
  region: "North East England",
  stage: "Diligence",
  riskRating: "Medium",
  impactTheme: "Clean Energy",
  targetIRR: "14.7%",
  ticketSize: "£5m–£25m",
  projectValue: "£300m",
  holdPeriod: "8–10 years",
  lastUpdated: "05/03/2026",
  heroImage: "https://images.unsplash.com/photo-1698508629476-cd445c7d70ac",
  heroImageAlt: "Offshore wind turbines standing in calm blue sea water during golden hour with clear sky",
  summary: `Northumbria Offshore Wind Phase II is a 180MW offshore wind development located 12km off the Northumberland coast. The project benefits from a 15-year Contract for Difference (CfD) awarded in AR5, providing long-term revenue certainty. Phase I (120MW) achieved commercial operations in Q3 2023 and has consistently outperformed P50 generation estimates by 4.2%. Phase II leverages the same grid connection, O&M infrastructure, and experienced development team, offering investors a de-risked entry into a proven offshore wind platform with strong ESG credentials and institutional-grade governance.`,
  highlights: [
  "15-year CfD contract providing revenue floor protection",
  "Phase I operational with 4.2% above P50 generation",
  "Full planning consent obtained — judicial review period expired",
  "Fixed-price EPC contract with Tier 1 contractor",
  "Investment-grade offtake counterparty (National Grid ESO)",
  "SFDR Article 9 compliant — ESG rating A+",
  "Experienced sponsor with £2.1bn AUM in renewables",
  "Tokenisation-ready structure for secondary liquidity"],

  timeline: [
  { milestone: "Financial Close", date: "30 Jun 2026", completed: false, active: true, description: "Target close with anchor investors committed" },
  { milestone: "Construction Start", date: "01 Sep 2026", completed: false, active: false, description: "EPC mobilisation and offshore installation begins" },
  { milestone: "First Power", date: "15 Mar 2027", completed: false, active: false, description: "First turbine energised and grid-connected" },
  { milestone: "Commercial Operations", date: "30 Jun 2027", completed: false, active: false, description: "Full 180MW capacity operational" },
  { milestone: "CfD Revenue Commencement", date: "01 Jul 2027", completed: false, active: false, description: "15-year CfD payments begin" },
  { milestone: "Target Exit Window", date: "2033–2035", completed: false, active: false, description: "Secondary sale or refinancing event" }],

  sponsor: {
    name: "Meridian Energy Partners",
    type: "Infrastructure Developer",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1906e56fb-1772697140188.png",
    logoAlt: "Modern office building exterior with glass facade representing Meridian Energy Partners headquarters",
    trackRecord: "18 years",
    aum: "£2.1bn",
    hq: "London, UK"
  },
  dealMetrics: [
  { label: "Total Equity", value: "£75m" },
  { label: "Senior Debt", value: "£195m" },
  { label: "Gearing", value: "65% / 35%" },
  { label: "DSCR (Min)", value: "1.35x" },
  { label: "Equity IRR", value: "14.7%" },
  { label: "MOIC", value: "2.6x" },
  { label: "Cash Yield", value: "7.8% p.a." },
  { label: "Payback", value: "6.2 years" }],

  coInvestors: [
  "Aviva Investors Infrastructure",
  "LGIM Real Assets",
  "Macquarie Asset Management"]

};

const IBERIAN_OPPORTUNITY = {
  id: "iberian-storage-opp",
  name: "Iberian Battery Storage Complex",
  sector: "Energy Transition",
  region: "Catalonia, Spain",
  stage: "SPV Formation",
  riskRating: "Medium",
  impactTheme: "Energy Storage",
  targetIRR: "13.8%",
  ticketSize: "£7.5m–£25m",
  projectValue: "£90m",
  holdPeriod: "10–12 years",
  lastUpdated: "10/04/2026",
  heroImage: "https://images.unsplash.com/photo-1624397640148-949b1732bb0a",
  heroImageAlt: "Grid-scale battery energy storage system with solar panels in background",
  summary: `Iberian Battery Storage Complex is an 800MWh grid-scale battery energy storage system (BESS) located in Catalonia, with capacity market revenues and merchant arbitrage optionality. The project holds a 10-year capacity market contract from Spain's REE, providing a stable revenue floor, with upside from intraday price arbitrage. Greenfield Renewables is leading the SPV formation and inviting co-investors at the first close. The deal is structured as a Limited Partnership with tokenisation capability for secondary liquidity.`,
  highlights: [
  "800MWh grid-scale BESS — largest capacity in Iberian market",
  "10-year capacity market contract with REE (Red Eléctrica)",
  "Merchant optionality on intraday arbitrage (~35% revenue)",
  "SPV formation underway — first close target 30 Jun 2026",
  "Co-investment allocation: £20m (22.2% of equity)",
  "SFDR Article 8 compliant — ESG assessment complete",
  "Tokenisation-ready structure for secondary liquidity",
  "Experienced sponsor with £2.1bn AUM in renewables"],

  timeline: [
  { milestone: "IC Approval",        date: "15 Mar 2026", completed: true,  active: false, description: "Investment Committee approved deal terms" },
  { milestone: "SPV Established",    date: "01 Mar 2026", completed: true,  active: false, description: "Iberian Battery Storage SPV Ltd incorporated" },
  { milestone: "First Capital Call", date: "10 Apr 2026", completed: true,  active: false, description: "Capital call notice issued to confirmed LPs" },
  { milestone: "First Close",        date: "30 Jun 2026", completed: false, active: true,  description: "Target close with anchor investors committed" },
  { milestone: "Asset Acquisition",  date: "30 Sep 2026", completed: false, active: false, description: "Completion of acquisition and transfer of title" },
  { milestone: "Target Exit Window", date: "2036–2038",   completed: false, active: false, description: "Secondary sale or refinancing event" }],

  sponsor: {
    name: "Greenfield Renewables",
    type: "Infrastructure Fund Manager",
    logo: null,
    logoAlt: "Greenfield Renewables fund manager logo",
    trackRecord: "12 years",
    aum: "£2.1bn",
    hq: "London, UK"
  },
  dealMetrics: [
  { label: "Total Equity",       value: "£90m" },
  { label: "Senior Debt",        value: "£0m" },
  { label: "Gearing",            value: "Equity only" },
  { label: "Equity IRR",         value: "13.8%" },
  { label: "MOIC",               value: "2.4x" },
  { label: "Cash Yield",         value: "8.2% p.a." },
  { label: "Payback",            value: "7.1 years" },
  { label: "Co-Inv. Allocation", value: "£20m (22.2%)" }],

  coInvestors: [
  "Apex Infrastructure Partners",
  "Meridian Capital Partners"]
};

const OPPORTUNITIES_MAP = {
  "opp-001": OPPORTUNITY,
  "iberian-storage-opp": IBERIAN_OPPORTUNITY,
};

export default function OpportunityDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role } = useRole();
  const opportunity = OPPORTUNITIES_MAP[id] ?? OPPORTUNITIES_MAP["opp-001"];
  const [activeTab, setActiveTab] = useState("overview");
  const [showICMemo, setShowICMemo] = useState(false);
  const [toast, setToast] = useState(null);

  const showToastMsg = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const renderTab = () => {
    switch (activeTab) {
      case "overview":return <OverviewTab opportunity={opportunity} />;
      case "financials":return <FinancialsTab />;
      case "risks":return <KeyRisksTab />;
      case "esg":return <ESGTab />;
      case "documents":return <DocumentsTab />;
      case "dealterms":return <DealTermsTab />;
      default:return <OverviewTab opportunity={opportunity} />;
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Toast */}
        {toast &&
        <div className="fixed bottom-6 right-6 z-50 bg-foreground text-background px-4 py-3 rounded-lg shadow-lg font-caption text-sm flex items-center gap-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            {toast}
          </div>
        }

        {/* IC Memo Modal */}
        {showICMemo && <ICMemoView opportunity={opportunity} onClose={() => setShowICMemo(false)} />}

        {/* Header */}
        <OpportunityHeader
          opportunity={opportunity}
          userRole={role} />
        

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Content */}
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-xl mx-auto">
          {/* Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <QuickActionsPanel context="opportunity-detail" userRole={role} layout="horizontal" />
            <div className="flex gap-2">
              <Button
                variant="outline"
                iconName="FileText"
                iconPosition="left"
                iconSize={15}
                onClick={() => setShowICMemo(true)}>
                
                IC Summary
              </Button>
              {(role === ROLES.CO_INVESTOR || role === ROLES.EXTERNAL_INVESTOR) &&
              <Button
                variant="default"
                iconName="Handshake"
                iconPosition="left"
                iconSize={15}
                onClick={() => showToastMsg('Interest registered — Greenfield Renewables will be in touch within 48 hours.')}>
                Express Interest
              </Button>
              }
              {(role === ROLES.INVESTMENT_TEAM || role === ROLES.PLATFORM_ADMIN) &&
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                iconSize={15}
                onClick={() => navigate(ROUTES.spvCreation)}>

                  Create SPV
                </Button>
              }
            </div>
          </div>

          {/* Tab Content */}
          {renderTab()}
        </div>

        {/* Footer */}
        <footer className="border-t border-border mt-12 px-4 md:px-6 lg:px-8 py-6">
          <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="font-caption text-xs text-muted-foreground">
              © {new Date()?.getFullYear()} SeedDrive Infrastructure Equity. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs font-caption text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="Shield" size={12} />
                FCA Regulated
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Lock" size={12} />
                Secure Data Room
              </span>
              <span className="flex items-center gap-1">
                <Icon name="FileCheck" size={12} />
                AIFMD Compliant
              </span>
            </div>
          </div>
        </footer>
      </div>
    </AppLayout>);

}